import React, { useEffect, useState } from "react";
import { User, CalendarDays, Mail, Shield, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Profile() {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch logged-in agent’s info
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please log in first.");
          setLoading(false);
          return;
        }

        const res = await fetch(`${SERVER_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (res.ok) {
          setAgent(data);
        } else {
          toast.error(data.message || "Failed to load profile.");
        }
      } catch {
        toast.error("Error fetching profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, []);

  // 🧮 Format Date Safely
  const formatDate = (dateString) => {
    if (!dateString) return "Date unavailable";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Date unavailable"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-green-600">
        <Loader2 size={32} className="animate-spin mb-3" />
        <p>Loading profile...</p>
      </div>
    );

  if (!agent)
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500">
        No agent data found.
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white p-6 md:p-12 mt-16">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-green-100 p-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center gap-8 border-b pb-6 mb-8">
          <div className="bg-green-100 p-6 rounded-full shadow-inner">
            <User className="h-14 w-14 text-green-700" />
          </div>

          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-green-800">
              {agent.username || "Unnamed Agent"}
            </h1>
            <p className="text-green-600 mt-1 font-medium">
              {agent.isAdmin ? "Administrator" : "Sales Agent"}
            </p>

            <div className="mt-4 space-y-2 text-gray-700">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Mail className="w-5 h-5 text-green-500" />
                <span>{agent.email || "No email provided"}</span>
              </div>
              <div className="flex items-center justify-center md:justify-start gap-2">
                <CalendarDays className="w-5 h-5 text-green-500" />
                <span>Joined {formatDate(agent.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Role Info */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl flex items-center gap-5 shadow-inner">
          <div className="bg-white p-4 rounded-full shadow-md">
            <Shield className="h-8 w-8 text-green-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-green-800">System Role</h3>
            <p className="text-gray-700 mt-1 leading-relaxed">
              {agent.isAdmin
                ? "You have full administrative access — including managing distributors, viewing sales records, and updating inventory."
                : "You’re authorized to manage product sales, record stock updates, and track your performance within the system."}
            </p>
          </div>
        </div>

        {/* Optional Stats / Future Section */}
        <div className="mt-10 bg-white border-t border-green-100 pt-6 text-center text-gray-500 text-sm">
          🌱 Keep growing with Green World — every sale makes an impact!
        </div>
      </div>
    </div>
  );
}

export default Profile;
